import { useId, useState } from "react";
import { CORE_WEB_VITALS, VITAL_LABEL, VITAL_RATING_LABEL, WEB_VITAL_METRICS } from "@metrickle/schema/constants";
import type { VitalBucket, VitalMetricOrCwv, VitalRow, VitalSegment, VitalsReport } from "@metrickle/schema";
import { useTasks, useVitals } from "../lib/api";
import { useStatsParams } from "../lib/params";
import { A11Y_LABEL, fmtMoney, fmtNumber, fmtPercent, fmtVital, roundPoints } from "../lib/format";
import { AppHeader, useAppId } from "../components/app-header";
import { StatsToolbar } from "../components/layout";
import { StatTile } from "../components/stats";
import { Button, Callout, Card, CardEmpty, CardHeader, Delta, Drawing, Empty, ErrorState, SelectField, Skeleton, StatGrid, TabBar, Table, TableScroll } from "../components/ui";

const METRICS: VitalMetricOrCwv[] = ["CWV", ...WEB_VITAL_METRICS];
const METRIC_ITEMS = METRICS.map((m) => ({ value: m, label: m === "CWV" ? "All three" : m }));
const SEGMENT_METRICS: VitalMetricOrCwv[] = ["CWV", ...CORE_WEB_VITALS];
const DEVICE_LABEL: Record<string, string> = { desktop: "Desktop", mobile: "Phone", tablet: "Tablet" };
const NO_TASK = "__none";


/** What slowness costs: web vitals per page and site-wide, and how visits with slow loads convert. */
export function Speed() {
  const appId = useAppId();
  const [task, setTask] = useState<string | undefined>();
  const [metric, setMetric] = useState<VitalMetricOrCwv>("CWV");
  const panelId = useId();
  const q = useVitals(appId, { task });
  const r = q.data;

  return (
    <>
      <AppHeader title="What slowness costs" tabTitle="Speed" />
      <StatsToolbar appId={appId} />
      {q.isError ? (
        <Card>
          <ErrorState error={q.error} />
        </Card>
      ) : q.isPending && !r ? (
        <Skeleton className="h-96" />
      ) : !r || r.measured === 0 ? (
        <Card>
          <Empty title="No speed measurements in this period" illustration={<Drawing name="emptyPeriod" />}>
            The web SDK reports web vitals (LCP, INP, CLS, FCP and TTFB) automatically on every page load. Native apps don't report them, so
            they show nothing here. If you use the web SDK, widen the date range.
          </Empty>
        </Card>
      ) : (
        <div className="space-y-4">
          <Tiles report={r} />
          <Callout role="note">
            Slow visits convert less. Part of that is slow devices and networks, not only your pages. Fix the slowest page and watch this number.
          </Callout>
          <Card className="min-w-0">
            <CardHeader
              title="Slowest pages, by what they cost"
              subtitle="Conversions each page lost because some loads were slow, compared with visits where it loaded fast. Select a page to filter everything by it."
              actions={
                <TabBar aria-label="Metric" panelId={panelId} items={METRIC_ITEMS} value={metric} onChange={(v) => setMetric(v as VitalMetricOrCwv)} />
              }
            />
            <div id={panelId} role="tabpanel" aria-label={VITAL_LABEL[metric].label}>
              <p className="px-4 pb-2 text-xs text-ink-3">
                {VITAL_LABEL[metric].label}: {VITAL_LABEL[metric].hint.replace(/^./, (c) => c.toLowerCase())}.
              </p>
              <Pages rows={r.pages.filter((p) => p.metric === metric).sort((a, b) => b.lost - a.lost)} metric={metric} />
            </div>
          </Card>
          <Segments segments={r.segments} metric={SEGMENT_METRICS.includes(metric) ? metric : "CWV"} />
          <TaskSpeed appId={appId} task={task} onTask={setTask} report={r} loading={q.isFetching} />
        </div>
      )}
    </>
  );
}

function Tiles({ report }: { report: VitalsReport }) {
  const { lost } = report;
  const lostHint = [
    `Conversions short of what fast visits convert at${lost.money != null ? `, worth about ${fmtMoney(lost.money)}` : ""}.`,
    lost.clear ? "" : "An estimate: not clear of noise yet.",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <Card>
      <StatGrid cols={5}>
        <StatTile
          label="Conversions lost to slowness"
          value={`${lost.clear ? "" : "≈ "}${fmtNumber(Math.round(lost.conversions))}`}
          note={[lost.money != null && lost.money > 0 ? fmtMoney(lost.money) : null, lost.clear ? null : "Estimate, not clear of noise yet"].filter(Boolean).join(" · ") || undefined}
          hint={lostHint}
        />
        <StatTile
          label="Visits passing Core Web Vitals"
          value={report.passing == null ? "—" : fmtPercent(report.passing)}
          hint="Share of measured visits where LCP, INP and CLS were all good."
        />
        {CORE_WEB_VITALS.map((m) => {
          const row = report.site.find((s) => s.metric === m);
          const rating = row?.p75Rating ? `${VITAL_RATING_LABEL[row.p75Rating]}. ` : "";
          return (
            <StatTile
              key={m}
              label={`${m}, 75th percentile`}
              value={
                <>
                  {fmtVital(m, row?.p75 ?? null)}
                  {row?.p75Rating && <span className="ml-2 text-sm font-medium text-ink-2">{VITAL_RATING_LABEL[row.p75Rating]}</span>}
                </>
              }
              hint={`${rating}${VITAL_LABEL[m].hint}.`}
            />
          );
        })}
      </StatGrid>
    </Card>
  );
}

/** Conversion rate of every slower bucket together. */
function slowRate(buckets: VitalBucket[]): number | null {
  const slow = buckets.filter((b) => b.rating !== "good");
  const sessions = slow.reduce((n, b) => n + b.sessions, 0);
  return sessions ? slow.reduce((n, b) => n + b.converted, 0) / sessions : null;
}

const rate = (n: number | null | undefined) => (n == null ? "—" : fmtPercent(n));

/** Conversions lost, marked as an estimate (in words, not only "≈") until clear of noise. */
function Lost({ lost, money, clear }: { lost: number; money: number | null; clear: boolean }) {
  const n = Math.round(lost);
  if (n === 0) return <span className="text-ink-3">None</span>;
  return (
    <>
      <span className="font-medium text-ink">
        {!clear && (
          <>
            <span aria-hidden="true">≈ </span>
            <span className="sr-only">about </span>
          </>
        )}
        {fmtNumber(n)}
      </span>
      {money != null && money > 0 && <span className="ml-1.5 text-xs text-ink-2">{fmtMoney(money)}</span>}
      {!clear && <div className="text-xs text-ink-3">Too early to tell</div>}
    </>
  );
}

function Pages({ rows, metric }: { rows: VitalRow[]; metric: VitalMetricOrCwv }) {
  const params = useStatsParams();
  if (!rows.length) return <CardEmpty divided>No pages with {metric === "CWV" ? "Core Web Vitals" : metric} measured in this period.</CardEmpty>;
  return (
    <TableScroll label="Slowest pages" divided>
      <Table caption={`Pages by conversions lost to slow loads, measured by ${VITAL_LABEL[metric].label}. Select a page to filter by it.`} className="min-w-[760px]">
        <thead>
          <tr>
            <th scope="col">Page</th>
            <th scope="col" className="text-right">Loads</th>
            <th scope="col" className="text-right">
              p75<span className="sr-only"> (75th percentile)</span>
            </th>
            <th scope="col" className="text-right">Poor loads</th>
            <th scope="col" className="text-right">Converts when fast</th>
            <th scope="col" className="text-right">When slow</th>
            <th scope="col" className="text-right">Lost</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p) => {
            const fast = p.buckets.find((b) => b.rating === "good");
            return (
              <tr key={p.path} className="align-top">
                <td className="max-w-64 truncate font-mono text-[13px] text-ink">
                  <Button variant="link" className="max-w-full" onClick={() => params.addFilter({ field: "path", op: "eq", value: p.path! })}>
                    <span className="truncate">{p.path}</span>
                  </Button>
                </td>
                <td className="text-right text-ink-2">{fmtNumber(p.samples)}</td>
                <td className="whitespace-nowrap text-right">
                  <span className="font-medium text-ink">{metric === "CWV" ? "—" : fmtVital(metric, p.p75)}</span>
                  {p.p75Rating && <div className="text-xs text-ink-2">{VITAL_RATING_LABEL[p.p75Rating]}</div>}
                </td>
                <td className="text-right text-ink-2">{fmtPercent(p.shares.poor)}</td>
                <td className="text-right text-ink-2">{rate(fast?.rate)}</td>
                <td className="text-right text-ink-2">{rate(slowRate(p.buckets))}</td>
                <td className="whitespace-nowrap text-right">
                  <Lost lost={p.lost} money={p.money} clear={p.clear} />
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    </TableScroll>
  );
}

function segmentLabel(key: string): string {
  const [kind, value = ""] = key.split(":");
  if (kind === "device") return DEVICE_LABEL[value] ?? value;
  return A11Y_LABEL[value] ?? value;
}

function Segments({ segments, metric }: { segments: VitalSegment[]; metric: VitalMetricOrCwv }) {
  const params = useStatsParams();
  const rows = segments.filter((s) => s.metric === metric);
  const filterOf = (key: string) => {
    const [kind, value = ""] = key.split(":");
    return { field: kind === "device" ? ("device_type" as const) : ("a11y" as const), op: "eq" as const, value };
  };
  return (
    <Card className="min-w-0">
      <CardHeader
        title="Who slowness hits hardest"
        subtitle={`How slow ${metric === "CWV" ? "loads" : `${metric} loads`} are by device and accessibility setting, and how fast and slow visits convert for each. Settings come from the moment of the load. Select a row to filter by it.`}
      />
      {!rows.length ? (
        <CardEmpty divided>Not enough measured visits per device or setting yet.</CardEmpty>
      ) : (
        <TableScroll label="Speed by device and accessibility setting" divided>
          <Table caption="Speed and conversion by device and accessibility setting, with how slow visits compare with fast ones. Select a row to filter by it." className="min-w-[640px]">
            <thead>
              <tr>
                <th scope="col">Who</th>
                <th scope="col" className="text-right">Visits</th>
                <th scope="col" className="text-right">
                  p75<span className="sr-only"> (75th percentile)</span>
                </th>
                <th scope="col" className="text-right">Poor loads</th>
                <th scope="col" className="text-right">Converts when fast</th>
                <th scope="col" className="text-right">When slow</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const pts = s.rateFast != null && s.rateSlow != null ? roundPoints(s.rateSlow - s.rateFast) : null;
                return (
                  <tr key={s.key}>
                    <td className="text-ink">
                      <Button variant="link" onClick={() => params.addFilter(filterOf(s.key))}>
                        {segmentLabel(s.key)}
                      </Button>
                    </td>
                    <td className="text-right text-ink-2">{fmtNumber(s.sessions)}</td>
                    <td className="text-right text-ink-2">{metric === "CWV" ? "—" : fmtVital(metric, s.p75)}</td>
                    <td className="text-right text-ink-2">{s.metric === "CWV" ? "—" : fmtPercent(s.poorShare)}</td>
                    <td className="text-right text-ink-2">{rate(s.rateFast)}</td>
                    <td className="whitespace-nowrap text-right">
                      <span className="font-medium text-ink">{rate(s.rateSlow)}</span>
                      {pts != null && (
                        <>
                          <span aria-hidden="true">
                            <Delta points current={s.rateSlow!} previous={s.rateFast!} vs="vs fast" className="ml-1.5" />
                          </span>
                          <span className="sr-only">
                            {pts === 0 ? ", same as fast" : `, ${Math.abs(pts)} percentage ${Math.abs(pts) === 1 ? "point" : "points"} ${pts < 0 ? "lower" : "higher"} than fast`}
                          </span>
                        </>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </TableScroll>
      )}
    </Card>
  );
}

/** Task success split by the worst rating seen during each attempt. */
function TaskSpeed({ appId, task, onTask, report, loading }: { appId: string; task: string | undefined; onTask: (t: string | undefined) => void; report: VitalsReport; loading: boolean }) {
  const tasks = useTasks(appId);
  if (tasks.isError)
    return (
      <Card className="min-w-0">
        <CardHeader title="Task success by speed" />
        <ErrorState title="Couldn't load your saved tasks" error={tasks.error} />
      </Card>
    );
  if (!tasks.data?.length) return null;
  const items = [{ value: NO_TASK, label: "Choose a saved task" }, ...tasks.data.map((t) => ({ value: t.id, label: t.name }))];
  return (
    <Card className="min-w-0">
      <CardHeader
        title="Task success by speed"
        subtitle="How often people finished a saved task, split by the slowest load during the attempt."
        actions={<SelectField aria-label="Saved task" items={items} value={task ?? NO_TASK} onChange={(v) => onTask(v === NO_TASK ? undefined : v)} />}
      />
      {!task ? (
        <CardEmpty divided>Choose a saved task to see whether slow loads stop people finishing it.</CardEmpty>
      ) : loading && !report.task ? (
        <Skeleton className="m-4 h-32" />
      ) : !report.task?.length ? (
        <CardEmpty divided>No measured attempts at this task in this period.</CardEmpty>
      ) : (
        <TableScroll label="Task success by speed" divided>
          <Table caption="Task success by the slowest rating during each attempt, for each measure.">
            <thead>
              <tr>
                <th scope="col">Measure</th>
                <th scope="col">Speed</th>
                <th scope="col" className="text-right">Attempts</th>
                <th scope="col" className="text-right">Success</th>
              </tr>
            </thead>
            {report.task.map((t) => (
              <tbody key={t.metric}>
                {t.buckets.map((b, i) => (
                  <tr key={b.rating}>
                    {i === 0 && (
                      <th scope="rowgroup" rowSpan={t.buckets.length} className="text-left align-top font-medium text-ink">
                        {t.metric === "CWV" ? "Core Web Vitals" : t.metric}
                      </th>
                    )}
                    <td className="text-ink">{VITAL_RATING_LABEL[b.rating]}</td>
                    <td className="text-right text-ink-2">{fmtNumber(b.attempts)}</td>
                    <td className="text-right font-medium text-ink">{fmtPercent(b.rate)}</td>
                  </tr>
                ))}
              </tbody>
            ))}
          </Table>
        </TableScroll>
      )}
    </Card>
  );
}
